/**
 * v3 subscription catalog day-off mapping (admin `subscriptions` listing API).
 *
 * Priority:
 * 1. Plan `no_meals_on_days` (non-empty) → use admin values; ignore initial `week_day_off`.
 * 2. Else category `no_meals_on_days` (non-empty) → same.
 * 3. Else both empty → fall back to initial API `week_day_off` (global org setting).
 *
 * Admin API:
 * - `max_day_off` — total off-days per week the customer may have selected (forced + optional).
 * - `no_meals_on_days` — `{ day: 0–6, is_forced? }` (moment Sun=0 … Sat=6).
 * - `is_forced: true` — always off; counts toward `max_day_off`; not togglable.
 *
 * Checkout cap: effective `max_day_off` is `min(plan max, 6)` (plan max wins when lower).
 *
 * Initial API `week_day_off` (global, Mon=1 … Sun=7):
 * - `0` or `-1` — no global off-day; all weekdays are optional selectable chips.
 * - `1`–`7` — that weekday is forced off; remaining weekdays are optional chips.
 */
import type { PlanDayOffOption } from '../../checkout';

export type CatalogDayOffSource = Record<string, unknown> | null | undefined;

const MOMENT_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;

/** v3 checkout date popup: never allow more than this many off-days per week. */
export const CHECKOUT_MAX_DAY_OFF_CAP = 6;

/** Initial API `week_day_off` (Mon=1 … Sun=7) → moment weekday (Sun=0 … Sat=6). */
export function orgWeekDayOffToMomentDay(week_day_off: number): number | null {
  const w = Number(week_day_off);
  if (w === 0 || w === -1) {
    return null;
  }
  if (w === 7) {
    return 0;
  }
  if (w >= 1 && w <= 6) {
    return w;
  }
  return null;
}

function coerceMomentWeekday(day: unknown): number | null {
  const n = Number(day);
  if (!Number.isInteger(n) || n < 0 || n > 6) {
    return null;
  }
  return n;
}

function coerceForcedFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true';
}

/** Normalize one admin off-day entry (object or legacy bare weekday number). */
export function normalizeV3PlanDayOffEntry(raw: unknown): PlanDayOffOption | null {
  if (raw == null) {
    return null;
  }
  if (typeof raw === 'number' || typeof raw === 'string') {
    const day = coerceMomentWeekday(raw);
    return day == null ? null : { day, is_forced: false };
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const row = raw as Record<string, unknown>;
    const day = coerceMomentWeekday(row.day);
    if (day == null) {
      return null;
    }
    return { day, is_forced: coerceForcedFlag(row.is_forced) };
  }
  return null;
}

/** Dedupe by weekday; forced wins when the same day appears twice. */
export function normalizeV3PlanDayOffList(raw: unknown): PlanDayOffOption[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    return [];
  }
  const by_day = new Map<number, PlanDayOffOption>();
  for (const entry of raw) {
    const normalized = normalizeV3PlanDayOffEntry(entry);
    if (!normalized) {
      continue;
    }
    const existing = by_day.get(normalized.day);
    if (!existing || normalized.is_forced) {
      by_day.set(normalized.day, {
        day: normalized.day,
        is_forced: Boolean(existing?.is_forced || normalized.is_forced),
      });
    }
  }
  return [...by_day.values()].sort((a, b) => a.day - b.day);
}

function getAdminNoMealsOnDaysRaw(plan: CatalogDayOffSource, category: CatalogDayOffSource): unknown[] | null {
  const plan_raw = plan?.no_meals_on_days;
  if (Array.isArray(plan_raw) && plan_raw.length > 0) {
    return plan_raw;
  }
  const category_raw = category?.no_meals_on_days;
  if (Array.isArray(category_raw) && category_raw.length > 0) {
    return category_raw;
  }
  return null;
}

/** True when admin listing API provided a non-empty `no_meals_on_days` (plan or category). */
export function hasAdminNoMealsOnDays(plan: CatalogDayOffSource, category?: CatalogDayOffSource): boolean {
  return getAdminNoMealsOnDaysRaw(plan, category ?? null) != null;
}

/** Build weekday options from initial API `week_day_off` when admin `no_meals_on_days` is empty. */
export function buildV3GlobalDayOffFallbackOptions(week_day_off: number): PlanDayOffOption[] {
  const forced_moment = orgWeekDayOffToMomentDay(week_day_off);
  if (forced_moment == null) {
    return MOMENT_WEEKDAYS.map(day => ({ day, is_forced: false }));
  }
  const options: PlanDayOffOption[] = [{ day: forced_moment, is_forced: true }];
  for (const day of MOMENT_WEEKDAYS) {
    if (day !== forced_moment) {
      options.push({ day, is_forced: false });
    }
  }
  return options;
}

/**
 * Resolved weekday options for checkout.
 * Admin non-empty → admin only. Empty admin → global `week_day_off` when provided.
 */
export function resolveV3CatalogPlanDayOffOptions(
  plan: CatalogDayOffSource,
  category?: CatalogDayOffSource,
  week_day_off?: number,
): PlanDayOffOption[] {
  const admin_raw = getAdminNoMealsOnDaysRaw(plan, category ?? null);
  if (admin_raw != null) {
    return normalizeV3PlanDayOffList(admin_raw);
  }
  if (week_day_off != null && Number.isFinite(Number(week_day_off))) {
    return buildV3GlobalDayOffFallbackOptions(Number(week_day_off));
  }
  return [];
}

/** `max_day_off` from plan → category; never below forced-day count. */
export function resolveV3CatalogPlanMaxDayOff(
  plan: CatalogDayOffSource,
  category?: CatalogDayOffSource,
  options?: PlanDayOffOption[],
): number {
  const resolved_options = options ?? resolveV3CatalogPlanDayOffOptions(plan, category);
  const forced_count = resolved_options.filter(o => o.is_forced).length;
  const raw = plan?.max_day_off ?? category?.max_day_off;
  let n = Number(raw);
  if (!Number.isFinite(n) || n < 0) {
    n = 1;
  }
  n = Math.floor(n);
  n = Math.min(n, CHECKOUT_MAX_DAY_OFF_CAP);
  return Math.max(n, forced_count, 1);
}

/** Attach normalized `max_day_off` + `no_meals_on_days` onto a catalog plan (admin-only unless `week_day_off` passed). */
export function applyV3CatalogPlanDayOffFields<T extends Record<string, unknown>>(
  plan: T,
  category?: CatalogDayOffSource,
  week_day_off?: number,
): T & { max_day_off: number; no_meals_on_days: PlanDayOffOption[] } {
  const no_meals_on_days = resolveV3CatalogPlanDayOffOptions(plan, category, week_day_off);
  const max_day_off = resolveV3CatalogPlanMaxDayOff(plan, category, no_meals_on_days);
  return {
    ...plan,
    max_day_off,
    no_meals_on_days,
  };
}

export type CheckoutDayOffState = {
  max_day_off: number;
  forced_day_offs: number[];
  temp_day_off_options: PlanDayOffOption[];
  day_off_array: number[];
};

/** Split resolved options into forced (pre-selected) vs optional chips. */
export function splitV3PlanDayOffOptions(options: PlanDayOffOption[]): {
  forced_day_offs: number[];
  temp_day_off_options: PlanDayOffOption[];
} {
  const forced_day_offs: number[] = [];
  const temp_day_off_options: PlanDayOffOption[] = [];
  for (const opt of options) {
    if (opt.is_forced) {
      forced_day_offs.push(opt.day);
    } else {
      temp_day_off_options.push(opt);
    }
  }
  return {
    forced_day_offs: [...new Set(forced_day_offs)].sort((a, b) => a - b),
    temp_day_off_options,
  };
}

/** Initial checkout off-day state for a v3 catalog plan. */
export function initV3CheckoutDayOffFromPlan(
  plan: Record<string, unknown>,
  week_day_off: number,
  category?: CatalogDayOffSource,
): CheckoutDayOffState {
  const options = resolveV3CatalogPlanDayOffOptions(plan, category, week_day_off);
  const { forced_day_offs, temp_day_off_options } = splitV3PlanDayOffOptions(options);
  const max_day_off = resolveV3CatalogPlanMaxDayOff(plan, category, options);

  return {
    max_day_off,
    forced_day_offs,
    temp_day_off_options,
    day_off_array: forced_day_offs.length > 0 ? [...forced_day_offs] : [],
  };
}
